/**
 * Augmentation selection validation
 */
import { getAugmentation } from "../augmentations/registry";
import type { SelectAugmentationAction } from "../types/augmentation";
import type { ValidationResult } from "../types/common";
import type { ChessState } from "../types/game-state";

/**
 * Validate an augmentation selection action
 * Returns ValidationResult with valid=true if the selection is allowed
 */
export function validateSelectAugmentation(
  state: ChessState,
  action: SelectAugmentationAction,
): ValidationResult {
  // 1. Check game phase
  if (state.phase !== "AUGMENTATION_SELECTION") {
    return {
      valid: false,
      error: "증강 선택 단계가 아닙니다",
      details: { phase: state.phase },
    };
  }

  // 2. Check augmentation selection state exists
  if (!state.augmentationSelection) {
    return {
      valid: false,
      error: "증강 선택 상태가 없습니다",
    };
  }

  // 3. Check player hasn't already selected
  const hasSelected =
    action.player === "WHITE"
      ? state.augmentationSelection.whiteSelected
      : state.augmentationSelection.blackSelected;

  if (hasSelected) {
    return {
      valid: false,
      error: "이미 증강을 선택했습니다",
      details: { player: action.player },
    };
  }

  // 4. Check augmentation ID is in the player's available choices
  const playerChoices =
    action.player === "WHITE"
      ? (state.augmentationSelection.whiteChoices ?? [])
      : (state.augmentationSelection.blackChoices ?? []);

  if (!playerChoices.includes(action.augmentationId)) {
    return {
      valid: false,
      error: "선택 가능한 증강이 아닙니다",
      details: {
        selected: action.augmentationId,
        available: playerChoices,
      },
    };
  }

  // 5. Check augmentation exists in registry
  const augmentation = getAugmentation(action.augmentationId);
  if (!augmentation) {
    return {
      valid: false,
      error: "잘못된 증강 ID입니다",
      details: { augmentationId: action.augmentationId },
    };
  }

  return { valid: true };
}
